/**
 * Enhanced Backdrop Toggle Action
 * Supports show/hide/toggle, custom z-index, opacity, and click actions.
 * Usage: window.dispatchEvent(new CustomEvent('arc:action:backdropToggle', { detail: { action: 'show', zIndex: 9999, onClick: 'hideModal' } }))
 */

export const runBackdropToggle = async (data = {}) => {
  try {
    const {
      action = 'toggle', // 'show', 'hide', 'toggle'
      id,
      selector,
      eventId,
      targetEventId,
      zIndex = 5,
      opacity = 0.5,
      backgroundColor = 'rgba(0, 0, 0, 0.5)',
      onClick = null, // action to trigger when backdrop is clicked
      onClickData = {}, // data to pass to the click action
      duration = 300, // animation duration in ms
      cursor = 'pointer'
    } = data;

    // Use targetEventId, eventId, or selector for unique ID generation
    const finalId = id || targetEventId || eventId || 'backdrop';

    // Debounce to prevent double execution
    const now = Date.now();
    if (runToggleBackdrop._lastCall && now - runToggleBackdrop._lastCall < 100) {
      return { success: true, message: 'Debounced duplicate call' };
    }
    runToggleBackdrop._lastCall = now;

    // Validate action
    const validActions = ['show', 'hide', 'toggle'];
    if (!validActions.includes(action)) {
      throw new Error(`Invalid action. Must be one of: ${validActions.join(', ')}`);
    }

    // Generate unique backdrop ID
    const backdropId = `backdrop-${finalId}`;
    let backdrop = document.getElementById(backdropId);

    // Helper: is open? (check if backdrop exists and is visible)
    const isOpen = backdrop && backdrop.style.opacity !== '0' && backdrop.style.display !== 'none';

    // Action logic
    const doShow = () => {
      // Prevent opening if already animating or open
      if (backdrop && (backdrop.dataset.isAnimating === 'true' || isOpen)) {
        if (window.enableDebugMode) { console.log('Backdrop already open or animating, skipping show'); }
        return;
      }

      // Create backdrop if it doesn't exist
      if (!backdrop) {
        backdrop = document.createElement('div');
        backdrop.id = backdropId;
        backdrop.style.position = 'fixed';
        backdrop.style.top = '0';
        backdrop.style.left = '0';
        backdrop.style.width = '100vw';
        backdrop.style.height = '100vh';
        backdrop.style.backgroundColor = backgroundColor;
        backdrop.style.zIndex = zIndex.toString();
        backdrop.style.opacity = '0';
        backdrop.style.transition = `opacity ${duration}ms cubic-bezier(.4,0,.2,1)`;
        backdrop.style.cursor = cursor;
        backdrop.style.display = 'block';

        // Add click handler to always hide and unmount backdrop, then optionally fire custom event
        const handleBackdropClick = (e) => {
          // Always hide and unmount the backdrop on click
          doHide(true); // pass force flag
          // Trigger the specified action (if any)
          if (onClick) {
            window.dispatchEvent(
              new CustomEvent(`arc:action:${onClick}`, {
                detail: {
                  ...onClickData,
                  triggeredBy: 'backdrop',
                  backdropId: backdropId
                }
              })
            );

            window.dispatchEvent(
              new CustomEvent(onClick, {
                detail: {
                  ...onClickData,
                  triggeredBy: 'backdrop',
                  backdropId: backdropId
                }
              })
            );
          }
        };
        backdrop.addEventListener('click', handleBackdropClick);
        backdrop._clickHandler = handleBackdropClick;

        document.body.appendChild(backdrop);
      }

      // Set animation flag
      backdrop.dataset.isAnimating = 'true';

      // Start with opacity 0
      backdrop.style.opacity = '0';
      backdrop.style.display = 'block';

      // Force reflow to ensure transition
      void backdrop.offsetWidth;

      // Animate in
      requestAnimationFrame(() => {
        backdrop.style.opacity = opacity.toString();
      });

      setTimeout(() => {
        // Clear animation flag
        backdrop.dataset.isAnimating = 'false';
      }, duration);
    };

    const doHide = (force = false) => {
      // Prevent closing if already animating or closed, unless forced
      if (!backdrop || (!force && (backdrop.dataset.isAnimating === 'true' || !isOpen))) {
        if (window.enableDebugMode) { console.log('Backdrop already closed or animating, skipping hide'); }
        return;
      }
      // Set animation flag during closing
      backdrop.dataset.isAnimating = 'true';
      // Animate out
      requestAnimationFrame(() => {
        backdrop.style.opacity = '0';
      });
      setTimeout(() => {
        // Remove backdrop from DOM
        if (backdrop && backdrop._clickHandler) {
          backdrop.removeEventListener('click', backdrop._clickHandler);
          delete backdrop._clickHandler;
        }
        if (backdrop) {
          backdrop.remove();
        }
        // Clean up reference
        backdrop = null;
      }, duration);
    };

    if (action === 'show') {
      doShow();
    } else if (action === 'hide') {
      doHide();
    } else if (action === 'toggle') {
      isOpen ? doHide() : doShow();
    }

    return {
      success: true,
      message: `Backdrop ${action} executed`,
      data: { action, id: backdropId, zIndex, opacity, backgroundColor, onClick, onClickData, duration }
    };
  } catch (error) {
    console.error('Error toggling backdrop:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

export default runBackdropToggle;
